import { UUID } from '@beelday/common'
import { Extracted } from 'feed/feed-notifications'
import { useEffect, useState } from 'react'
import { unreadChatCount, unreadFeedCount } from './model/unread'

export type UnreadCounts = {
	chat: number
	feed: number
}

type Props = {
	trainingId: UUID
	chatOpen: boolean
	feedOpen: boolean
	onChange: (counts: UnreadCounts) => void
}

/**
 * Unread chat messages and new boardlet posts, as plain numbers.
 *
 * They come from the headless web components the desktop menu uses; only the
 * presentation differs. `open` tells each component its panel is being read,
 * which is what clears its count. Elements are held through callback refs so
 * the listeners attach on the first mount, not after some later re-render.
 */
export const UnreadCountsSource = ({
	trainingId,
	chatOpen,
	feedOpen,
	onChange,
}: Props): JSX.Element => {
	const [chatElement, setChatElement] = useState<HTMLElement | null>(null)
	const [feedElement, setFeedElement] = useState<HTMLElement | null>(null)
	const [chat, setChat] = useState(0)
	const [feed, setFeed] = useState(0)

	useEffect(() => {
		if (!chatElement) {
			return
		}
		const listener = (event: Event) =>
			setChat(unreadChatCount((event as CustomEvent<unknown>).detail))
		chatElement.addEventListener('new-message', listener)
		return () => chatElement.removeEventListener('new-message', listener)
	}, [chatElement])

	useEffect(() => {
		if (!feedElement) {
			return
		}
		const listener = (event: Event) =>
			setFeed(
				unreadFeedCount((event as CustomEvent<Partial<Extracted>>).detail)
			)
		feedElement.addEventListener('feed-change', listener)
		return () => feedElement.removeEventListener('feed-change', listener)
	}, [feedElement])

	useEffect(() => {
		onChange({ chat, feed })
	}, [chat, feed, onChange])

	return (
		<>
			<beelday-chat-notifications
				trainingid={trainingId}
				open={chatOpen ? 'true' : 'false'}
				ref={setChatElement}
			/>
			<beelday-training-feed-notifications
				trainingid={trainingId}
				open={feedOpen ? 'true' : 'false'}
				postid="none"
				ref={setFeedElement}
			/>
		</>
	)
}

export default UnreadCountsSource
